import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { HTMLAttributes, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { radius } from "src/design/tokens";
import { InlineAlert, LoadingButton, UploadArea } from "src/grading/components";
import { errorMessage } from "src/grading/utils/apiError";
import { LONG_REQUEST_TIMEOUT_MS } from "src/store/apiSlice";
import {
  useCreateDpaUploadMutation,
  usePutDataPrivacyAgreementMutation,
} from "src/store/reducers/districts/districtsApiSlice";

/** Agreement rules (data-model, "DPA DTO"). */
export const DPA_VERSION_MAX = 40;
export const DPA_SIGNED_BY_MAX = 200;
export const DPA_FILE_MAX_BYTES = 10 * 1024 * 1024;

/** Today as `YYYY-MM-DD` in the viewer's time zone, the latest signed date allowed. */
export const todayIso = (now = new Date()) => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** A real calendar date in `YYYY-MM-DD` form (rejects 2026-02-30). */
export const isIsoDate = (value: string) => {
  if (!ISO_DATE.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return (
    !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value
  );
};

export interface DpaFields {
  version: string;
  signedBy: string;
  signedAt: string;
  file: File | null;
}

export type DpaFieldErrors = Partial<Record<keyof DpaFields, string>>;

/** Translation keys of the problems with an agreement before it is sent; empty when it is valid. */
export const validateDpa = (
  fields: DpaFields,
  today = todayIso(),
): Partial<Record<keyof DpaFields, string>> => {
  const errors: Partial<Record<keyof DpaFields, string>> = {};
  const version = fields.version.trim();
  const signedBy = fields.signedBy.trim();
  if (!version) errors.version = "district.dpaVersionRequired";
  else if (version.length > DPA_VERSION_MAX)
    errors.version = "district.dpaVersionTooLong";
  if (!signedBy) errors.signedBy = "district.dpaSignedByRequired";
  else if (signedBy.length > DPA_SIGNED_BY_MAX)
    errors.signedBy = "district.dpaSignedByTooLong";
  if (!fields.signedAt) errors.signedAt = "district.dpaSignedAtRequired";
  else if (!isIsoDate(fields.signedAt))
    errors.signedAt = "district.dpaSignedAtInvalid";
  else if (fields.signedAt > today)
    errors.signedAt = "district.dpaSignedAtFuture";
  if (fields.file) {
    const pdf =
      fields.file.type === "application/pdf" ||
      fields.file.name.toLowerCase().endsWith(".pdf");
    if (!pdf) errors.file = "district.dpaFileType";
    else if (fields.file.size > DPA_FILE_MAX_BYTES)
      errors.file = "district.dpaFileTooLarge";
  }
  return errors;
};

/** Sends the PDF to the presigned PUT; throws when storage refuses it or it takes too long. */
export const putToPresignedUrl = async (url: string, file: File) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), LONG_REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(url, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": "application/pdf" },
      signal: controller.signal,
    });
    if (!response.ok) throw new Error(`upload status ${response.status}`);
  } finally {
    clearTimeout(timer);
  }
};

class UploadError extends Error {}

export interface DpaDialogProps {
  open: boolean;
  districtId: string;
  /** True when an agreement is already on file, so this replaces it. */
  replacing?: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const BLANK: DpaFields = {
  version: "",
  signedBy: "",
  signedAt: "",
  file: null,
};

/**
 * Records or replaces the district's data privacy agreement (FR-038, FR-039): version, who signed
 * it, the signed date, and optionally the signed PDF (10 MB or less), uploaded through a presigned
 * PUT before the agreement is saved.
 */
export const DpaDialog = ({
  open,
  districtId,
  replacing = false,
  onClose,
  onSaved,
}: DpaDialogProps) => {
  const { t } = useTranslation();
  const titleId = useId();
  const [fields, setFields] = useState<DpaFields>(BLANK);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadKey, setUploadKey] = useState(0);
  const [createUpload] = useCreateDpaUploadMutation();
  const [putAgreement] = usePutDataPrivacyAgreementMutation();

  useEffect(() => {
    if (!open) {
      setFields(BLANK);
      setTouched(false);
      setError(null);
      setUploadKey((k) => k + 1);
    }
  }, [open]);

  const today = todayIso();
  const errors = validateDpa(fields, today);
  const shown = (key: keyof DpaFields) => {
    const problem = errors[key];
    return (touched || key === "file") && problem ? t(problem) : undefined;
  };
  const edit = (patch: Partial<DpaFields>) => {
    setFields((f) => ({ ...f, ...patch }));
    setError(null);
  };

  const save = async () => {
    setTouched(true);
    setError(null);
    if (Object.keys(errors).length > 0) return;
    setSaving(true);
    try {
      let documentUploadId: string | undefined;
      if (fields.file) {
        const upload = await createUpload({ districtId }).unwrap();
        try {
          await putToPresignedUrl(upload.url, fields.file);
        } catch {
          throw new UploadError();
        }
        documentUploadId = upload.uploadId;
      }
      await putAgreement({
        districtId,
        version: fields.version.trim(),
        signedBy: fields.signedBy.trim(),
        signedAt: fields.signedAt,
        ...(documentUploadId ? { documentUploadId } : {}),
      }).unwrap();
      onSaved();
    } catch (e) {
      setError(
        e instanceof UploadError
          ? t("district.dpaUploadError")
          : errorMessage(t, e, t("district.dpaSaveError")),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      aria-labelledby={titleId}
      fullWidth
      maxWidth="sm"
      PaperProps={
        { "data-testid": "dpa-dialog" } as HTMLAttributes<HTMLDivElement>
      }
    >
      <DialogTitle id={titleId} component="h2" sx={{ fontSize: 24 }}>
        {replacing
          ? t("district.dpaReplaceTitle")
          : t("district.dpaRecordTitle")}
      </DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
      >
        <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
          {t("district.dpaIntro")}
        </Typography>
        <TextField
          label={t("district.dpaVersion")}
          value={fields.version}
          onChange={(e) => edit({ version: e.target.value })}
          required
          autoFocus
          error={Boolean(shown("version"))}
          helperText={shown("version") ?? t("district.dpaVersionHelper")}
          inputProps={{
            "data-testid": "dpa-version",
            maxLength: DPA_VERSION_MAX + 10,
          }}
        />
        <TextField
          label={t("district.dpaSignedBy")}
          value={fields.signedBy}
          onChange={(e) => edit({ signedBy: e.target.value })}
          required
          error={Boolean(shown("signedBy"))}
          helperText={shown("signedBy") ?? t("district.dpaSignedByHelper")}
          inputProps={{
            "data-testid": "dpa-signed-by",
            maxLength: DPA_SIGNED_BY_MAX + 10,
          }}
        />
        <TextField
          type="date"
          label={t("district.dpaSignedAt")}
          value={fields.signedAt}
          onChange={(e) => edit({ signedAt: e.target.value })}
          required
          InputLabelProps={{ shrink: true }}
          error={Boolean(shown("signedAt"))}
          helperText={shown("signedAt")}
          inputProps={{ "data-testid": "dpa-signed-at", max: today }}
        />
        <Box>
          <Typography
            component="p"
            sx={{ fontSize: 14, fontWeight: 700, mb: 0.75 }}
          >
            {t("district.dpaFile")}
          </Typography>
          <UploadArea
            key={uploadKey}
            accept=".pdf,application/pdf"
            inputTestId="dpa-file"
            disabled={saving}
            hint={t("district.dpaFileHint")}
            onFile={(file) => edit({ file })}
            onClear={() => edit({ file: null })}
          />
          {shown("file") ? (
            <Box
              role="alert"
              sx={(theme) => ({
                mt: 1,
                px: 1.5,
                py: 1,
                borderRadius: `${radius.panel}px`,
                bgcolor: theme.palette.dangerSoft,
                color: theme.palette.dangerInk,
                fontSize: 14,
              })}
            >
              {shown("file")}
            </Box>
          ) : null}
        </Box>
        {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1.25 }}>
        <Button onClick={onClose} disabled={saving} color="inherit">
          {t("common.cancel")}
        </Button>
        <LoadingButton
          variant="contained"
          color="secondary"
          data-testid="dpa-save"
          loading={saving}
          onClick={() => void save()}
          label={t("district.dpaSave")}
        />
      </DialogActions>
    </Dialog>
  );
};

export default DpaDialog;
